import React, { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { api, errMsg } from "@/lib/api";

const fmtCell = (v) => (typeof v === "number" ? new Intl.NumberFormat("nb-NO", { maximumFractionDigits: 2 }).format(v).replace(/\u00a0/g, " ") : String(v));
const colOf = (cell) => (cell || "").replace(/\d+/g, "");
const rowOf = (cell) => Number((cell || "").replace(/\D+/g, "")) || null;

/**
 * The customer's spreadsheet as cells, read-only, with the referenced row/cell highlighted and scrolled into view.
 * Values only (as Excel computed them) – this is for checking a number against its source, not for editing.
 */
export function SheetGrid({ caseId, docId, sheet, row, cell, className, maxHeight = "70vh" }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [active, setActive] = useState(sheet || null);
  const hiRef = useRef(null);
  const hiRow = rowOf(cell) || Number(row) || null;
  const hiCol = colOf(cell) || null;

  useEffect(() => {
    let alive = true;
    setErr(null);
    api.get(`/projects/${caseId}/documents/${docId}/sheet`, { params: active ? { sheet: active } : {} })
      .then((r) => { if (alive) { setData(r.data); setActive(r.data.sheet); } })
      .catch((e) => alive && setErr(errMsg(e)));
    return () => { alive = false; };
  }, [caseId, docId, active]);

  useEffect(() => {
    if (data && hiRef.current) hiRef.current.scrollIntoView({ block: "center", inline: "center" });
  }, [data, hiRow, hiCol]);

  if (err) return <div className="rounded-[10px] bg-[hsl(0_50%_46%/0.06)] px-3 py-2 text-[12px] text-[hsl(var(--v-blocked))]" data-testid="sheet-grid-error">{err}</div>;
  if (!data) return <div className="flex h-40 items-center justify-center text-muted-foreground" data-testid="sheet-grid-loading"><Loader2 className="h-4 w-4 animate-spin" /></div>;
  const onSheet = (s) => active === s;
  return (
    <div className={cn("flex min-h-0 flex-col", className)} data-testid="sheet-grid" data-sheet={data.sheet} data-highlight={cell || (hiRow ? `rad ${hiRow}` : undefined)}>
      {data.sheets.length > 1 ? (
        <div className="mb-2 flex flex-wrap gap-1" role="tablist" aria-label="Ark" data-testid="sheet-tabs">
          {data.sheets.map((s) => <button key={s} type="button" role="tab" aria-selected={onSheet(s)} onClick={() => setActive(s)} className={cn("h-7 rounded-full px-2.5 text-[12px] font-medium", onSheet(s) ? "bg-[hsl(var(--brand))] text-white" : "bg-black/[0.05] text-foreground/70 hover:bg-black/[0.08]")} data-testid={`sheet-tab-${s}`}>{s}</button>)}
        </div>
      ) : null}
      <div className="min-h-0 w-fit max-w-full overflow-auto rounded-[10px] border border-black/[0.08] bg-white scrollbar-thin" style={{ maxHeight }}>
        <table className="border-separate border-spacing-0 font-mono text-[11.5px]">
          <thead className="sticky top-0 z-10">
            <tr>
              <th className="sticky left-0 z-20 border-b border-r border-black/[0.08] bg-[hsl(210_20%_97%)] px-2 py-1 text-left text-[10.5px] font-medium text-muted-foreground">#</th>
              {data.columns.map((c) => <th key={c} className={cn("border-b border-black/[0.08] bg-[hsl(210_20%_97%)] px-2 py-1 text-left text-[10.5px] font-medium text-muted-foreground", c === hiCol && "bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-strong))]")}>{c}</th>)}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => {
              const map = Object.fromEntries(r.cells.map((x) => [x.c, x.v]));
              const isRow = r.r === hiRow;
              return (
                <tr key={r.r} ref={isRow && !hiCol ? hiRef : undefined} className={cn(isRow && "bg-[hsl(var(--brand-soft))]/60")} data-testid={`sheet-row-${r.r}`} data-highlight={isRow || undefined}>
                  <td className={cn("sticky left-0 z-[1] border-b border-r border-black/[0.06] bg-[hsl(210_20%_98.5%)] px-2 py-0.5 text-right text-[10.5px] text-muted-foreground", isRow && "font-semibold text-[hsl(var(--brand-strong))]")}>{r.r}</td>
                  {data.columns.map((c) => {
                    const v = map[c];
                    const hit = isRow && c === hiCol;
                    return (
                      <td key={c} ref={hit ? hiRef : undefined} className={cn("max-w-[320px] truncate border-b border-black/[0.05] px-2 py-0.5 align-top", typeof v === "number" && "text-right tabular-nums", hit && "rounded-[4px] bg-[hsl(var(--brand))] font-semibold text-white ring-2 ring-[hsl(var(--primary))]")} title={v == null ? undefined : String(v)} data-testid={hit ? "sheet-highlight" : undefined}>
                        {v == null ? "" : fmtCell(v)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {data.truncated ? <p className="mt-1 text-[11px] text-muted-foreground" data-testid="sheet-grid-truncated">Viser de første {data.rows.length} radene med innhold – åpne originalen for resten.</p> : null}
    </div>
  );
}
